import { useState, useEffect } from "react";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { todayLocalISO } from "@/lib/date-utils";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface Procedimento {
  descricao: string;
  valor: number;
  dente?: string;
}

export interface OrdemServico {
  id: string;
  user_id?: string;
  numero_os: string;
  paciente_id: string;
  dentista_id: string;
  data_abertura: string;
  data_prevista: string | null;
  data_conclusao: string | null;
  procedimentos: Procedimento[];
  valor_bruto?: number;
  valor_desconto?: number;
  valor_total: number;
  status: string;
  prioridade: string;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateOrdemServicoData {
  paciente_id: string;
  dentista_id: string;
  data_abertura?: string;
  data_prevista?: string;
  data_conclusao?: string;
  procedimentos: Procedimento[];
  valor_bruto?: number;
  valor_desconto?: number;
  valor_total: number;
  status?: string;
  prioridade?: string;
  observacoes?: string;
}

export function useOrdemServico() {
  const [ordensServico, setOrdensServico] = useState<OrdemServico[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const fetchOrdensServico = async () => {
    if (!user) {
      setLoading(false);
      setOrdensServico([]);
      return;
    }
    try {
      setLoading(true);
      const data = await apiList<OrdemServico>("ordem_servico");
      setOrdensServico(data.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
    } catch (error: any) {
      toast.error("Erro ao carregar ordens de servico: " + error.message);
      setOrdensServico([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrdensServico();
  }, [user?.id, activeClinicaId]);

  const createOrdemServico = async (osData: CreateOrdemServicoData) => {
    try {
      if (!user) {
        toast.error("Usuario nao autenticado");
        return;
      }
      const numero = `OS-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      const data = await apiCreate<OrdemServico>("ordem_servico", {
        numero_os: numero,
        paciente_id: osData.paciente_id,
        dentista_id: osData.dentista_id,
        data_abertura: osData.data_abertura || todayLocalISO(),
        data_prevista: osData.data_prevista || null,
        data_conclusao: osData.data_conclusao || null,
        procedimentos: osData.procedimentos,
        valor_bruto: osData.valor_bruto ?? osData.valor_total,
        valor_desconto: osData.valor_desconto || 0,
        valor_total: osData.valor_total,
        status: osData.status || "Aberta",
        prioridade: osData.prioridade || "Normal",
        observacoes: osData.observacoes || null,
      });
      setOrdensServico((prev) => [data, ...prev]);
      toast.success("Ordem de servico criada com sucesso!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao criar ordem de servico: " + error.message);
      throw error;
    }
  };

  const updateOrdemServico = async (id: string, osData: Partial<CreateOrdemServicoData>) => {
    try {
      const data = await apiUpdate<OrdemServico>("ordem_servico", id, osData as Record<string, unknown>);
      setOrdensServico((prev) => prev.map((os) => (os.id === id ? data : os)));
      toast.success("Ordem de servico atualizada com sucesso!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao atualizar ordem de servico: " + error.message);
      throw error;
    }
  };

  const deleteOrdemServico = async (id: string) => {
    try {
      await apiDelete("ordem_servico", id);
      setOrdensServico((prev) => prev.filter((os) => os.id !== id));
      toast.success("Ordem de servico excluida com sucesso!");
    } catch (error: any) {
      toast.error("Erro ao excluir ordem de servico: " + error.message);
      throw error;
    }
  };

  return { ordensServico, loading, createOrdemServico, updateOrdemServico, deleteOrdemServico, refetch: fetchOrdensServico };
}
